import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React from 'react';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {Pill} from 'src/design/atoms/Pill';
import type {IFormFieldV2} from 'src/types/FormV2';
import {FormFieldTypeDisplayNameV2} from 'src/types/FormV2';

export interface FieldSchemaSummaryProps {
  fieldList: IFormFieldV2[];
}

/**
 * Read-only schema summary for an equipment type's inspection fields —
 * shows the field configuration (type, required, options, measurement
 * settings) rather than a rendered form. Used on the view page so users see
 * what was configured, not a pointer-events-disabled fake form (the shared
 * `app/form-builder` `FormPreview`, which stays reserved for the editor's
 * live preview).
 */
export const FieldSchemaSummary: React.FC<FieldSchemaSummaryProps> = ({
  fieldList,
}) => {
  if (fieldList.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No fields to display
      </Text>
    );
  }

  return (
    <Stack gap="md">
      {fieldList.map(field => (
        <FieldSchemaSummaryCard
          key={field.invariantId ?? field.name}
          field={field}
        />
      ))}
    </Stack>
  );
};

const FieldSchemaSummaryCard: React.FC<{field: IFormFieldV2}> = ({field}) => {
  const rows = getFieldSettingRows(field);

  return (
    <SectionCard
      title={field.label}
      rightSection={
        <Group gap="xs">
          {field.required && <Pill text="Required" color="yellow" />}
          <Pill text={FormFieldTypeDisplayNameV2[field.type]} color="blue" />
        </Group>
      }>
      <Stack gap="xs" p="md">
        {field.description && (
          <Text size="sm" c="dimmed">
            {field.description}
          </Text>
        )}
        {rows.map(row => (
          <Group key={row.label} gap="xs" wrap="nowrap" align="flex-start">
            <Text size="sm" fw={500} c="dimmed" miw={110}>
              {row.label}
            </Text>
            <Text size="sm">{row.value}</Text>
          </Group>
        ))}
        {rows.length === 0 && !field.description && (
          <Text size="sm" c="dimmed">
            No additional settings configured.
          </Text>
        )}
      </Stack>
    </SectionCard>
  );
};

interface SettingRow {
  label: string;
  value: string;
}

function getFieldSettingRows(field: IFormFieldV2): SettingRow[] {
  const rows: SettingRow[] = [];

  if (field.labelTooltip) {
    rows.push({label: 'Help Text', value: field.labelTooltip});
  }

  if (
    (field.type === 'select' || field.type === 'select-multiple') &&
    (field.options?.length ?? 0) > 0
  ) {
    rows.push({
      label: 'Options',
      value: field.options!.map(opt => opt.label).join(', '),
    });
  }

  if (field.type === 'measurement') {
    const units = field.measurementOptions?.units ?? [];
    if (units.length > 0) {
      rows.push({label: 'Units', value: units.join(', ')});
    }
    if (field.measurementOptions?.minValue !== undefined) {
      rows.push({
        label: 'Minimum Value',
        value: String(field.measurementOptions.minValue),
      });
    }
    if (field.measurementOptions?.maxValue !== undefined) {
      rows.push({
        label: 'Maximum Value',
        value: String(field.measurementOptions.maxValue),
      });
    }
    if (field.measurementOptions?.decimalPlaces !== undefined) {
      rows.push({
        label: 'Decimal Places',
        value: String(field.measurementOptions.decimalPlaces),
      });
    }
  }

  if (field.type === 'number') {
    if (field.validation?.min !== undefined) {
      rows.push({label: 'Minimum Value', value: String(field.validation.min)});
    }
    if (field.validation?.max !== undefined) {
      rows.push({label: 'Maximum Value', value: String(field.validation.max)});
    }
  }

  if (field.type === 'text') {
    if (field.textboxOptions?.multiline) {
      rows.push({label: 'Multiline', value: 'Yes'});
    }
    if (field.validation?.maxLength !== undefined) {
      rows.push({
        label: 'Max Length',
        value: String(field.validation.maxLength),
      });
    }
    if (field.validation?.pattern) {
      rows.push({label: 'Pattern', value: field.validation.pattern});
    }
  }

  return rows;
}
